import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { PrimaryButton } from "./ui";
import { JurisdictionField } from "./JurisdictionField";
import { useAppState } from "../AppStateContext";
import type { Project } from "../api";
import {
  isHiddenJurisdiction,
  jurisdictionPatchCode,
  optionsWithCurrent,
} from "../jurisdiction";
import { useJurisdictions } from "../useJurisdictions";
import { useI18n } from "../i18n";
import "./Dialog.css";

/**
 * Per-project jurisdiction editor, opened from the project pill's "⋯" menu.
 * Seeds the project's current jurisdiction — including a retired one, which the
 * catalog no longer lists — and only PATCHes when the user picks a different
 * enabled code (see jurisdictionPatchCode), so a retired value is shown but
 * never sent back.
 */
export function ProjectJurisdictionDialog({
  project,
  onClose,
}: {
  project: Project;
  onClose: () => void;
}) {
  const { updateProjectJurisdiction } = useAppState();
  const { t } = useI18n();
  const { options: catalog } = useJurisdictions();
  // A hidden current jurisdiction (the default `ru`) is not offered, so start
  // from the empty placeholder rather than a value with no matching option.
  const [selected, setSelected] = useState(
    isHiddenJurisdiction(project.jurisdiction_code) ? "" : project.jurisdiction_code ?? "",
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const options = optionsWithCurrent(catalog, {
    code: project.jurisdiction_code,
    name: project.jurisdiction_name,
  });

  const onSave = async () => {
    const code = jurisdictionPatchCode({
      selected,
      current: project.jurisdiction_code,
      enabledCodes: catalog.map((j) => j.code),
    });
    // No real change (empty, unchanged, or a retired current re-selected) —
    // nothing to PATCH, so just close.
    if (!code) {
      onClose();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await updateProjectJurisdiction(project.project_id, code);
      onClose();
    } catch (err) {
      setBusy(false);
      setError(err instanceof Error ? err.message : t("jurisdiction.saveFailed"));
    }
  };

  return createPortal(
    <div className="dialog-backdrop" onMouseDown={onClose}>
      <div
        className="dialog"
        role="dialog"
        aria-modal="true"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="dialog__head">
          <h2>{t("jurisdiction.dialogTitle")}</h2>
          <button type="button" aria-label={t("common.close")} onClick={onClose}>
            ×
          </button>
        </header>
        <div className="dialog__body">
          <JurisdictionField
            options={options}
            value={selected}
            onChange={setSelected}
            placeholder={t("jurisdiction.placeholder")}
          />
          {error ? <p className="auth-error">{error}</p> : null}
          <div className="dialog__actions">
            <button type="button" className="btn--outline" onClick={onClose}>
              {t("common.cancel")}
            </button>
            <PrimaryButton
              type="button"
              className="btn--auto"
              disabled={busy}
              onClick={onSave}
            >
              {t("common.save")}
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
